import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Calendar, Users,
  CheckCircle, Mail, Send,
  BarChart3, ChevronRight, Pin, Star,
  MessageSquare, MapPin, RefreshCw, FileText,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores';
import { useAnnouncements } from '../hooks/useAnnouncements';
import { useFamilyMembers, useFamilyMembersCount } from '../hooks/useFamilyMembers';
import { useUpcomingEvents } from '../hooks/useEvents';
import { parseEventDate } from '../utils/formatters';
import { useUserBalance } from '../hooks/usePayments';
import { useMyDuesStatus } from '../hooks/useDues';
import { useMySubscription } from '../hooks/useSubscription';
import { useImageUrl } from '../hooks/useImageUrl';
import { usersService } from '../services/membersService';
import { formatCurrency } from '../utils/payment-format';
import { getMemberDisplayName, getMemberDisplayFirstName } from '../utils/name';
import { CURRENT_REUNION_SLUG, REUNION } from '../config/organization';
import { MarkdownRenderer } from '../components/ui/MarkdownRenderer';
import { Badge } from '../components/ui/Badge';
import { Avatar } from '../components/ui/Avatar';
import { BranchProgressCard } from '../components/dues/BranchProgressCard';

// ---------------------------------------------------------------------------
// Countdown
// ---------------------------------------------------------------------------

const REUNION_DATE = new Date(`${REUNION.startDate}T00:00:00`);

interface CountdownValues {
  months: number;
  days: number;
  hours: number;
  minutes: number;
}

function getCountdown(target: Date): CountdownValues {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return { months: 0, days: 0, hours: 0, minutes: 0 };

  const totalMinutes = Math.floor(diff / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  return {
    months: Math.floor(totalDays / 30),
    days: totalDays % 30,
    hours: totalHours % 24,
    minutes: totalMinutes % 60,
  };
}

const CountdownBox: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-brand rounded-lg flex items-center justify-center shadow-md">
      <span className="text-white text-xl sm:text-2xl font-bold tabular-nums">
        {String(value).padStart(2, '0')}
      </span>
    </div>
    <span className="text-xs text-text-tertiary mt-1.5 uppercase tracking-wide">{label}</span>
  </div>
);

// ---------------------------------------------------------------------------
// Member avatar for directory preview
// ---------------------------------------------------------------------------

const MemberAvatar: React.FC<{
  member: { id: string; firstName: string; lastName: string; nickname?: string; profileImage?: string };
}> = ({ member }) => {
  const imageUrl = useImageUrl(member.profileImage);
  const displayFirst = getMemberDisplayFirstName(member);
  return (
    <Link
      to={`/members/${member.id}`}
      className="flex-shrink-0 group"
      title={getMemberDisplayName(member)}
    >
      <Avatar
        firstName={displayFirst}
        lastName={member.lastName}
        src={imageUrl}
        size="md"
        className="ring-2 ring-white group-hover:ring-brand transition-all"
      />
    </Link>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const diffDays = Math.ceil(Math.abs(Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
}

function formatEventDate(dateString: string, endDateString?: string): string {
  const startDate = parseEventDate(dateString);
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: startDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  };
  if (endDateString) {
    const endDate = parseEventDate(endDateString);
    return `${startDate.toLocaleDateString('en-US', options)} – ${endDate.toLocaleDateString('en-US', options)}`;
  }
  return startDate.toLocaleDateString('en-US', options);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const MemberHomePage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(getCountdown(REUNION_DATE));

  // Live countdown — update every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(getCountdown(REUNION_DATE));
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Data fetching
  const { data: announcementsData, isLoading: announcementsLoading } = useAnnouncements({
    limit: 3,
    orderBy: 'publishedAt',
    orderDirection: 'desc',
  });

  const { data: familyMembersCount } = useFamilyMembersCount();

  const { data: membersData } = useFamilyMembers({ limit: 8, directoryOnly: true });
  const previewMembers = membersData?.members ?? [];

  const { data: upcomingEventsData, isLoading: eventsLoading } = useUpcomingEvents({ limit: 3 });

  const { data: balanceData, isLoading: balanceLoading } = useUserBalance();
  const { data: duesData } = useMyDuesStatus();
  const { data: subscriptionData } = useMySubscription();
  const balance = balanceData?.data?.balance;
  const assessment = duesData?.data?.assessment;
  const subscription = subscriptionData?.data?.subscription;
  const hasActiveSubscription = subscription && subscription.status === 'active';

  const { data: userWithMember } = useQuery({
    queryKey: ['user-with-member', user?.id],
    queryFn: () => usersService.getUserWithMember(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
  const profileImageUrl = useImageUrl(userWithMember?.familyMember?.profileImage);

  // Loading state
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-64 text-text-primary">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4" />
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      {/* ================================================================ */}
      {/* Welcome                                                          */}
      {/* ================================================================ */}
      <section className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold text-text-primary">
            Welcome home, {userWithMember?.familyMember ? getMemberDisplayFirstName(userWithMember.familyMember) : (user.firstName || 'Member')}
          </h1>
          <p className="text-text-secondary mt-1">
            Here&rsquo;s what&rsquo;s happening with the family
          </p>
        </div>
        <Avatar
          firstName={user.firstName}
          lastName={user.lastName}
          src={profileImageUrl}
          size="lg"
          className="hidden sm:flex cursor-pointer"
          onClick={() => navigate('/profile')}
        />
      </section>

      {/* ================================================================ */}
      {/* Announcements                                                    */}
      {/* ================================================================ */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-semibold text-text-primary">
            Latest Announcements
          </h2>
          <Link
            to="/announcements"
            className="flex items-center gap-1 text-brand hover:text-brand-hover text-sm font-medium transition-colors"
          >
            View All
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {announcementsLoading ? (
          <div className="bg-surface-1 rounded-xl border border-border p-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-text-secondary text-sm">Loading announcements...</p>
          </div>
        ) : announcementsData?.announcements &&
          announcementsData.announcements.length > 0 ? (
          <div className="space-y-4">
            {announcementsData.announcements.map((announcement) => (
              <div
                key={announcement.id}
                className="bg-surface-1 rounded-xl border border-border p-5 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" size="sm">
                      {announcement.type}
                    </Badge>
                    {announcement.isPinned && (
                      <Pin className="w-3.5 h-3.5 text-text-tertiary" />
                    )}
                    {announcement.isFeatured && (
                      <Star className="w-3.5 h-3.5 text-warning" />
                    )}
                  </div>
                  <span className="text-xs text-text-tertiary whitespace-nowrap">
                    {formatRelativeDate(
                      announcement.publishedAt || announcement.createdAt
                    )}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-text-primary mb-1.5">
                  {announcement.title}
                </h3>
                <div className="text-sm text-text-secondary line-clamp-2 mb-3">
                  <MarkdownRenderer
                    content={announcement.excerpt || announcement.content}
                    proseColor="slate"
                    className="text-sm"
                  />
                </div>
                <Link
                  to="/announcements"
                  className="text-brand text-sm font-medium hover:text-brand-hover transition-colors"
                >
                  Read More &rarr;
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-surface-1 rounded-xl border border-border p-8 text-center">
            <MessageSquare className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
            <h3 className="text-sm font-semibold text-text-primary mb-1">
              No recent announcements
            </h3>
            <p className="text-text-secondary text-sm">
              Check back soon for family updates and news.
            </p>
          </div>
        )}
      </section>

      {/* ================================================================ */}
      {/* Dues + Reunion                                                   */}
      {/* ================================================================ */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Dues Card */}
        <div className="bg-surface-1 rounded-xl border border-border p-6 border-l-4 border-l-brand">
          <h2 className="font-display text-lg font-semibold text-text-primary mb-4">
            Your Dues
          </h2>

          {balanceLoading ? (
            <div className="space-y-3">
              <div className="h-7 w-32 bg-surface-2 rounded animate-pulse" />
              <div className="h-4 w-48 bg-surface-2 rounded animate-pulse" />
            </div>
          ) : balance ? (
            balance.outstanding > 0 ? (
              <div className="space-y-3">
                <p className="text-2xl font-bold text-amber-600">
                  {formatCurrency(balance.outstanding)}{' '}
                  <span className="text-base font-medium">
                    {hasActiveSubscription ? 'remaining' : 'due'}
                  </span>
                </p>
                {hasActiveSubscription ? (
                  <Badge variant="success" className="gap-1.5">
                    <RefreshCw className="w-3 h-3" />
                    Auto-Pay Active
                  </Badge>
                ) : assessment && (
                  <Badge
                    className={
                      assessment.status === 'overdue'
                        ? 'text-red-600 bg-red-100'
                        : assessment.status === 'partial'
                          ? 'text-blue-600 bg-blue-100'
                          : 'text-yellow-600 bg-yellow-100'
                    }
                  >
                    {assessment.status.charAt(0).toUpperCase() + assessment.status.slice(1)}
                  </Badge>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <p className="text-2xl font-bold text-green-600">Paid in Full</p>
                </div>
                <p className="text-sm text-text-secondary">
                  You&rsquo;re all caught up!
                </p>
              </div>
            )
          ) : (
            <p className="text-sm text-text-secondary">No dues assessed yet</p>
          )}

          <div className="flex flex-wrap gap-3 mt-6">
            <Link
              to="/dues"
              className="inline-flex items-center px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors text-sm font-medium"
            >
              Make a Payment
            </Link>
            <Link
              to="/dues"
              className="inline-flex items-center px-4 py-2 border border-border text-text-secondary rounded-lg hover:bg-surface-1 transition-colors text-sm font-medium"
            >
              View Payment History
              <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>

        {/* Reunion Countdown Card */}
        <div className="bg-surface-1 rounded-xl border border-border p-6 border-l-4 border-l-brand">
          <div className="mb-4">
            <h2 className="font-display text-lg font-semibold text-text-primary">
              {REUNION.title}
            </h2>
            <p className="text-sm text-text-tertiary">{REUNION.tagline}</p>
          </div>

          <div className="flex items-center gap-3 sm:gap-4 mb-4">
            <CountdownBox value={countdown.months} label="Months" />
            <CountdownBox value={countdown.days} label="Days" />
            <CountdownBox value={countdown.hours} label="Hours" />
            <CountdownBox value={countdown.minutes} label="Min" />
          </div>

          <div className="flex items-center gap-2 text-text-secondary text-sm mb-1">
            <Calendar className="w-4 h-4" />
            <span>{REUNION.dateLabel}</span>
          </div>
          <div className="flex items-center gap-2 text-text-secondary text-sm">
            <MapPin className="w-4 h-4" />
            <span>{REUNION.location}</span>
          </div>

          <Link
            to={`/events/${CURRENT_REUNION_SLUG}`}
            className="inline-flex items-center mt-6 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors text-sm font-medium"
          >
            View Reunion Details
            <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
      </section>

      {/* ================================================================ */}
      {/* Dues by Branch Progress                                          */}
      {/* ================================================================ */}
      <BranchProgressCard />

      {/* ================================================================ */}
      {/* Upcoming Events                                                  */}
      {/* ================================================================ */}
      <section className="space-y-6">
        {/* Upcoming Events */}
        <div>
          <h2 className="font-display text-xl font-semibold text-text-primary mb-4">
            Upcoming Events
          </h2>
          {eventsLoading ? (
            <div className="bg-surface-1 rounded-xl border border-border p-8 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand mx-auto mb-4" />
              <p className="text-text-secondary text-sm">Loading events...</p>
            </div>
          ) : upcomingEventsData?.events &&
            upcomingEventsData.events.length > 0 ? (
            <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory -mx-4 px-4 sm:mx-0 sm:px-0">
              {upcomingEventsData.events.map((event) => (
                <Link
                  key={event.id}
                  to={`/events/${event.slug || event.id}`}
                  className="flex-shrink-0 w-56 sm:w-64 bg-surface-1 rounded-xl border border-border p-5 hover:shadow-sm transition-shadow snap-start"
                >
                  <div className="flex items-center gap-2 text-brand text-xs font-semibold uppercase tracking-wide mb-2">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatEventDate(event.startDate, event.endDate ?? undefined)}
                  </div>
                  <h3 className="font-semibold text-text-primary text-sm mb-1">
                    {event.title}
                  </h3>
                  {event.location && (
                    <p className="flex items-center gap-1 text-text-tertiary text-xs">
                      <MapPin className="w-3 h-3" />
                      {event.location}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-surface-1 rounded-xl border border-border p-8 text-center">
              <Calendar className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
              <h3 className="text-sm font-semibold text-text-primary mb-1">
                No upcoming events
              </h3>
              <p className="text-text-secondary text-sm">
                Check back soon for family gatherings.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ================================================================ */}
      {/* Section 3: Family Directory                                      */}
      {/* ================================================================ */}
      <section className="bg-surface-1 rounded-xl border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-semibold text-text-primary">
              Family Directory
            </h2>
            <p className="text-text-secondary text-sm mt-0.5">
              {familyMembersCount ?? '–'} members in our family
            </p>
          </div>
          <Link
            to="/directory"
            className="flex items-center gap-1 text-brand hover:text-brand-hover text-sm font-medium transition-colors"
          >
            Browse Directory
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {previewMembers.length > 0 ? (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2">
            {previewMembers.map((member) => (
              <MemberAvatar key={member.id} member={member} />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-3 text-text-secondary">
            <Users className="w-5 h-5" />
            <span className="text-sm">Loading family members...</span>
          </div>
        )}
      </section>

      {/* ================================================================ */}
      {/* Section 4: Communication Center (Admin only)                     */}
      {/* ================================================================ */}
      {user &&
        ['admin', 'organizer', 'finance_manager'].includes(user.role) && (
          <section className="bg-surface-1 rounded-xl border border-border p-6">
            <h2 className="font-display text-lg font-semibold text-text-primary mb-4">
              Communication Center
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <Link
                to="/admin/communications"
                className="bg-surface-0 rounded-lg border border-border p-4 hover:bg-surface-2 transition-colors"
              >
                <Mail className="w-5 h-5 text-text-secondary mb-2" />
                <p className="font-medium text-text-primary text-sm">Dashboard</p>
                <p className="text-xs text-text-tertiary">View all</p>
              </Link>
              <Link
                to="/admin/communications/compose"
                className="bg-surface-0 rounded-lg border border-border p-4 hover:bg-surface-2 transition-colors"
              >
                <Send className="w-5 h-5 text-text-secondary mb-2" />
                <p className="font-medium text-text-primary text-sm">Compose</p>
                <p className="text-xs text-text-tertiary">Send email</p>
              </Link>
              <Link
                to="/admin/communications/templates"
                className="bg-surface-0 rounded-lg border border-border p-4 hover:bg-surface-2 transition-colors"
              >
                <FileText className="w-5 h-5 text-text-secondary mb-2" />
                <p className="font-medium text-text-primary text-sm">Templates</p>
                <p className="text-xs text-text-tertiary">Manage</p>
              </Link>
              <Link
                to="/admin/communications/analytics"
                className="bg-surface-0 rounded-lg border border-border p-4 hover:bg-surface-2 transition-colors"
              >
                <BarChart3 className="w-5 h-5 text-text-secondary mb-2" />
                <p className="font-medium text-text-primary text-sm">Analytics</p>
                <p className="text-xs text-text-tertiary">View metrics</p>
              </Link>
            </div>
          </section>
        )}
    </div>
  );
};

export { MemberHomePage };
